import { useCallback } from 'react';

import { useTranslations } from 'next-intl';
import { toast } from 'sonner';

import { useConfigValue } from '@/components/providers/config-provider';
import { useToasterId } from '@/components/toaster-context';
import { useTrackEvent } from '@/hooks/use-track-event';
import { CHAT_ANALYTICS_EVENTS } from '@/lib/constants/post-message-types';
import {
  findSensitiveData,
  type SensitiveDataProtectionConfig,
} from '@/lib/utils/sensitive-data-detector';

/**
 * Detects sensitive data in user input; on a match, tracks a value-free analytics
 * event and shows a localized toast naming the type and value.
 */
export function useSensitiveDataDetection() {
  const t = useTranslations();
  const toasterId = useToasterId();
  const trackEvent = useTrackEvent();
  const protectionConfig = useConfigValue<SensitiveDataProtectionConfig>(
    'security.sensitiveDataProtection',
  );

  const trackedHasSensitiveData = useCallback(
    (text: string): boolean => {
      const match = findSensitiveData({ text, config: protectionConfig ?? undefined });

      if (!match) {
        return false;
      }

      trackEvent({
        event: CHAT_ANALYTICS_EVENTS.PII_BLOCK,
        // Metadata only — never the matched value or message text. The event must not
        // carry the PII the block just prevented from leaving the client.
        properties: {
          category: match.category,
          ruleId: match.ruleId,
          direction: 'input',
          detectedSubtype: match.subtype,
          valueLength: match.value.length,
          messageLength: text.length,
        },
      });

      toast.error(
        t('sensitiveData.warning', {
          type: t(`sensitiveData.types.${match.category}`),
          value: match.value,
        }),
        {
          duration: 5000,
          toasterId,
        },
      );

      return true;
    },
    [t, protectionConfig, toasterId, trackEvent],
  );

  const preventSubmitIfSensitive = useCallback(
    ({ text, event }: { text: string; event: React.SyntheticEvent }): boolean => {
      if (trackedHasSensitiveData(text)) {
        event.preventDefault();
        event.stopPropagation();
        return true;
      }
      return false;
    },
    [trackedHasSensitiveData],
  );

  return { hasSensitiveData: trackedHasSensitiveData, preventSubmitIfSensitive };
}
